(ns app.front.view.inspections.deficiencies-v2
  (:require [app.domain.blackout.model :as blackout-model]
            [app.domain.building-code.model :as building-code-model]
            [app.domain.group.model :as group-model]
            [app.domain.inspection.model :as inspection-model]
            [app.domain.label.model :as label]
            [app.domain.util :as du]
            [app.domain.workspace.model :as workspace-model]
            [app.front.entities :as entities]
            [app.front.entity-edit :as ee]
            [app.front.file.download :as file-download]
            [app.front.process :as process]
            [app.front.ui :as ui]
            [app.front.ui.action-buttons :as ui-action-buttons]
            [app.front.ui.content-switcher :as content-switcher]
            [app.front.ui.grouped-menu :as ui-grouped-menu]
            [app.front.ui.icon-button :as ui-ib]
            [app.front.ui.multi-select :as ui-ms]
            [app.front.ui.photo-select :as photo-select]
            [app.front.ui.rich-text :as ui-rt]
            [app.front.ui.section :as section]
            [app.front.ui.select :as ui-select]
            [app.front.ui.separator :as ui-sep]
            [app.front.ui.sortable-table :as sortable-table]
            [app.front.ui.tabs :as ui-tabs]
            [app.front.ui.tags :as ui-tags]
            [app.front.util :as futil]
            [app.front.view.inspections.booking-utils :as butil]
            [app.front.view.inspections.subs :as inspection-subs]
            [app.front.view.workspace.subs :as ws-subs]
            [app.rich-text :as rich-text]
            [clojure.string :as str]
            [core.front.carbon :as cc]
            [core.front.date-util :as date-util]
            [core.front.event :refer [<sub >evt]]
            [core.front.feature-flags :as ff]
            [core.front.href :as href]
            [core.front.icons :as i]
            [core.util.util :as util]
            [goog.string :as gstr]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]
            [reagent.core :as r]))


(defn create-initial-state [editor-open?]
  {:create-open?       editor-open?
   :date               (js/Date.)
   :remark?            false
   :description        ""
   :photos-expanded?   false
   :selecting-photos?  false
   :selected-photo-ids #{}
   :selected-label-ids #{}})


(def interceptors [(rf/inject-cofx :tr)])


;; Setup DB to default state
;; Called from the view above
(rf/reg-event-fx ::initialize-view
  (fn [_ [_ workspace-id inspection-id]]
    {:dispatch [::initialize-db workspace-id inspection-id]}))


(rf/reg-event-db ::initialize-db
  (fn [db [_ workspace-id inspection-id]]
    (assoc db ::new-deficiency (create-initial-state false)
           ::workspace-id workspace-id
           ::inspection-id inspection-id)))


(rf/reg-sub ::all-deficiencies
  :<- [::entities/context]
  :<- [::inspection-id]
  (fn [[ctx inspection-id] _]
    (->> (entities/find-entities ctx
                                 {:entity/type   :entity.type/inspection-deficiency
                                  :entity/parent inspection-id})
         (filter du/is-active?))))


(rf/reg-sub ::deficiencies?
  :<- [::all-deficiencies]
  (fn [all-deficiencies _]
    (> (count all-deficiencies) 0)))


(rf/reg-sub ::visible-deficiencies?
  :<- [::deficiencies]
  (fn [deficiencies _]
    (> (count deficiencies) 0)))


(rf/reg-sub ::deficiencies
  :<- [::all-deficiencies]
  :<- [::show-all?]
  :<- [::tag-filter-ids]
  :<- [::entities/context]
  (fn [[all show-all? tag-filter-ids ctx] _]
    (let [tagged-ids (when (seq tag-filter-ids)
                       (->> (entities/find-entities ctx {:entity/type :entity.type/inspection-deficiency-label})
                            (filter label/deficiency-label-active?)
                            (filter #(contains? tag-filter-ids (-> % :inspection.deficiency-label/label :db/id)))
                            (map #(-> % :entity/parent :db/id))
                            set))]
      (->> all
           (filter (if show-all?
                     (constantly true)
                     #(-> % :domain/state (not= :inspection.deficiency.state/cleared))))
           (filter (if tagged-ids
                     #(contains? tagged-ids (:db/id %))
                     (constantly true)))
           (sort-by inspection-model/deficiency-sort-order)))))


(rf/reg-sub ::available-labels
  :<- [::available-label-groups]
  (fn [groups _]
    (->> groups
         (mapcat (fn [g] (map (fn [l] {:id (:db/id l) :name (label/label-name l)})
                              (label/active-labels g))))
         (sort-by :name))))


(rf/reg-sub ::tag-filter-ids
  (fn [db _] (::tag-filter-ids db #{})))


(rf/reg-event-db ::set-tag-filter
  (fn [db [_ ids]] (assoc db ::tag-filter-ids (set ids))))


(defn show-reference-handler [violations _]
  (some :inspection.deficiency/reference violations))

(rf/reg-sub ::show-reference-for-visit?
  :<- [::deficiencies]
  show-reference-handler)


(rf/reg-sub ::display-external-id?
  :<- [::deficiencies]
  (fn [deficiencies _]
    (some :inspection.deficiency/integration-sync-state deficiencies)))


;; Tab state management
(rf/reg-event-db ::set-active-tab
  (fn [db [_ tab]]
    (assoc db ::active-tab tab)))


(rf/reg-sub ::active-tab
  (fn [db _]
    (or (::active-tab db) :current)))


;; Workspace-wide deficiencies subscriptions
(rf/reg-sub ::workspace
  :<- [::entities/context]
  :<- [::workspace-id]
  (fn [[ctx workspace-id] _]
    (entities/find-entity ctx {:workspace/id workspace-id})))


(rf/reg-sub ::all-workspace-deficiencies
  :<- [::workspace]
  (fn [workspace _]
    (when workspace
      (->> workspace
           workspace-model/inspections
           (mapcat inspection-model/deficiencies)
           (filter du/is-active?)))))


(rf/reg-sub ::all-workspace-deficiencies-no-remarks
  :<- [::all-workspace-deficiencies]
  :<- [::show-all?]
  (fn [[deficiencies show-all?] _]
    (->> deficiencies
         (remove :inspection.deficiency/remark?)
         (filter (if show-all?
                   (constantly true)
                   #(-> % :domain/state (not= :inspection.deficiency.state/cleared))))
         (sort-by inspection-model/deficiency-sort-order))))


(rf/reg-sub ::all-workspace-remarks
  :<- [::all-workspace-deficiencies]
  (fn [deficiencies _]
    (->> deficiencies
         (filter :inspection.deficiency/remark?)
         (sort-by inspection-model/deficiency-sort-order))))


;; History tab expanded rows
(rf/reg-event-db ::toggle-history-row-expansion
  (rf/path [::history-expanded-rows])
  (fn [expanded-rows [_ id]]
    (util/toggle-in-set expanded-rows id)))


(rf/reg-sub ::history-expanded-rows
  (fn [db]
    (::history-expanded-rows db #{})))

(rf/reg-sub ::create-violations?
  :<- [::inspection-id]
  :<- [::inspection-phase]
  (fn [[inspection-id phase] _]
    (<sub [::process/signal-permitted? phase [:process.signal/create-inspection-violation {:inspection-id inspection-id}]])))

(rf/reg-sub ::create-deficiencies-unavailable?
  :<- [::inspection-id]
  :<- [::inspection-phase]
  (fn [[inspection-id phase] _]
    (contains? (<sub [::process/signal-errors phase [:process.signal/create-inspection-deficiency
                                                     {:inspection-id inspection-id}]])
               :process/illegal-signal)))

(rf/reg-sub ::create-signal
  :<- [::all-deficiencies]
  :<- [::create-violations?]
  :<- [::create-deficiencies-unavailable?]
  (fn ([[deficiencies create-violations? create-deficiencies-unavailable?] _]
       (cond
         (or (->> deficiencies
                  (some #(-> % :inspection.deficiency/subtype (= :inspection.deficiency.subtype/violation))))
             create-violations?)
         :process.signal/create-inspection-violation

         (not create-deficiencies-unavailable?)
         :process.signal/create-inspection-deficiency))))


(rf/reg-sub ::inspection-id
  :-> ::inspection-id)

(rf/reg-sub ::inspection
  :<- [::entities/context]
  :<- [::inspection-id]
  (fn [[ctx inspection-id] _]
    (entities/find-entity ctx inspection-id)))

(rf/reg-sub ::inspection-phase
  :<- [::inspection]
  :-> :entity/parent)

(rf/reg-sub ::phase-id
  :<- [::inspection-phase]
  :-> :db/id)

(rf/reg-event-db ::close-editor
  (fn [db _]
    (-> db
        (assoc ::new-deficiency (create-initial-state false))
        (dissoc ::ai-data)
        (dissoc ::selected-quick-pick-deficiency-ids)
        (dissoc ::quick-pick-visible-count))))


(rf/reg-event-db ::reset-editor
  (fn [db _]
    (assoc db ::new-deficiency (create-initial-state true))))


(rf/reg-event-db ::toggle-show-all
  (fn [db _]
    (update db ::show-all? not)))


(rf/reg-sub ::show-all?
  (fn [db _]
    (-> db ::show-all? boolean)))


(rf/reg-event-db ::toggle-row-expansion
  (fn [db [_ id]]
    (let [expanded? (-> db ::expanded-rows (contains? id))]
      (update db ::expanded-rows (comp set (if expanded? disj conj)) id))))

(rf/reg-sub ::expanded-rows
  (fn [db]
    (::expanded-rows db #{})))


(rf/reg-event-db ::toggle-photo-preview-expansion
  (rf/path [::expanded-photo-previews])
  (fn [previews [_ id]]
    (util/toggle-in-set previews id)))


(rf/reg-sub ::expanded-photo-preview?
  (fn [db [_ id]]
    (-> db ::expanded-photo-previews (contains? id))))


(rf/reg-sub ::time-slots
  (fn [[_ auth-group]]
    (rf/subscribe [::inspection-subs/availability auth-group (date-util/now)]))
  :-> :availability/time-slots)


(rf/reg-sub ::photos-for-linking
  (fn [[_ inspection-id]]
    (rf/subscribe [::entities/entities {:entity/type   :entity.type/file
                                        :entity/parent inspection-id}]))
  (fn [photos]
    (->> photos
         (remove :file/conversion-pending?)
         (filter :file/number)
         (sort-by :file/number))))


(defn- create-persist-command [db phase-id key signal success-msg]
  (let [inspection-id      (get db ::inspection-id)
        id                 (get-in db [key :id])
        description        (get-in db [key :description] "")
        reference          (get-in db [key :reference])
        remark?            (get-in db [key :remark?] false)
        automatic-booking? (get-in db [key :automatic-booking?])
        now                (js/Date.)
        comply-by-date     (get-in db [key :comply-by-date])
        date               (get-in db [key :date] now)
        photo-ids          (get-in db [key :selected-photo-ids])
        label-ids          (get-in db [key :selected-label-ids])
        payload            (p/assoc-if {:inspection-id                     inspection-id
                                        :inspection.deficiency/description description
                                        :inspection.deficiency/remark?     remark?
                                        :entity/created                    date}
                                       :id id
                                       :inspection.deficiency/reference reference
                                       :inspection.deficiency/comply-by-date comply-by-date
                                       :inspection.deficiency/comply-by-date-booking? automatic-booking?
                                       :inspection.deficiency/photos photo-ids
                                       :label-ids label-ids)
        editing?           (some? id)
        on-success         (if editing? ::deficiency-saved ::deficiency-created)]
    {:dispatch [::process/signal phase-id [signal payload] {:on-success [on-success]
                                                            :notify     {:success success-msg}}]}))


(defn- get-deficiency-id
  "Extracts the entity id of the deficiency affected by the reframe event response;
   the response is an entity-db update (a map with entity-id's as keys)."
  [resp]
  (let [deficiency-keys #{:domain/state :inspection.deficiency/remark?}] ; Exclusive to deficiencies in responses only
    (-> resp
        (p/find-first #(some deficiency-keys (-> % second keys)))
        first)))


(rf/reg-event-fx ::create
  (fn [{:keys [db]} [_ phase-id create-signal success-msg]]
    (create-persist-command db phase-id ::new-deficiency create-signal success-msg)))


(rf/reg-event-fx ::save
  (fn [{:keys [db]} [_ phase-id success-msg]]
    (create-persist-command db
                            phase-id
                            ::edit-deficiency
                            :process.signal/edit-inspection-deficiency
                            success-msg)))


(rf/reg-event-fx ::deficiency-created
  (fn [_ [_ _ resp]]
    (let [deficiency-id (entities/id-of-some-in-apply-changes :entity.type/inspection-deficiency resp)]
      {:dispatch-n [[::close-editor]
                    [::update-inspection-visit-result deficiency-id]
                    [::create-quick-pick-deficiency deficiency-id]]})))


(rf/reg-event-fx ::create-quick-pick-deficiency
  (fn [{:keys [db]} [_ deficiency-id]]
    (when (ff/enabled? :feature-flag.id/deficiency-quick-pick-list)
      (let [inspection-id (::inspection-id db)]
        {:comm/command {:command :inspection/create-quick-pick-deficiency
                        :method  :post
                        :data    (util/mmap inspection-id deficiency-id)}}))))


(rf/reg-event-fx ::deficiency-saved
  (fn [_ [_ _ resp]]
    {:dispatch-n [[::stop-editing]
                  [::update-inspection-visit-result (get-deficiency-id resp)]]}))


(rf/reg-event-fx ::deficiency-toggled
  interceptors
  (fn [_ [_ status resp]]
    {:dispatch-n [[::entities/apply-changes status resp]
                  [::update-inspection-visit-result (get-deficiency-id resp)]]}))


(rf/reg-event-fx ::update-inspection-visit-result
  ;; Bumps the ongoing inspection visit result from "passed" to unselected
  ;; when the user creates / saves / toggles a deficiency for that inspection (WIN-1057).
  (fn [{{::entities/keys [entity-db] :as db} :db} [_ order-or-deficiency-id]]
    (let [deficiency     (get entity-db order-or-deficiency-id)
          inspection-id  (:entity/parent deficiency)
          phase-id       (:entity/parent (get entity-db inspection-id))
          related-visit  (p/find-first (vals entity-db)
                                       #(and (= :entity.type/inspection-visit (:entity/type %))
                                             (= :inspection.visit.state/in-progress (:domain/state %))
                                             (= inspection-id (:entity/parent %))))
          should-update? (and (not (:inspection.deficiency/remark? deficiency))
                              (= :inspection.visit.result/passed (:inspection.visit/result related-visit))
                              (contains? #{:inspection.deficiency.state/active
                                           :inspection.deficiency.state/draft
                                           :inspection.order.state/active
                                           :inspection.order.state/draft}
                                         (:domain/state deficiency)))]
      (if should-update?
        {:dispatch [::ee/save
                    phase-id
                    :process.signal/update-inspection-visit
                    (:db/id related-visit)
                    :inspection.visit/result
                    nil ; Transformed to dissoc in backend
                    {}]}
        {:db db}))))


(rf/reg-event-db ::start-editing
  (fn [db [_ {id :db/id :as deficiency}]]
    (-> db
        (assoc ::edit-deficiency {:id                 id
                                  :description        (:inspection.deficiency/description deficiency)
                                  :reference          (:inspection.deficiency/reference deficiency)
                                  :date               (:entity/created deficiency)
                                  :comply-by-date     (:inspection.deficiency/comply-by-date deficiency)
                                  :remark?            (:inspection.deficiency/remark? deficiency)
                                  :selected-photo-ids (-> deficiency :inspection.deficiency/photos du/entity-id-set)
                                  :photos-expanded?   (-> db ::expanded-photo-previews (contains? id))
                                  :selecting-photos?  false})
        (update ::expanded-rows conj id))))


(rf/reg-event-db ::stop-editing
  (fn [db]
    (dissoc db ::edit-deficiency)))


(rf/reg-sub ::editor-submit-disabled?
  :<- [::get-new-deficiency-data :description]
  :<- [::get-new-deficiency-data :date]
  (fn [[description discovery-date] _]
    (or (empty? description)
        (nil? discovery-date))))


(rf/reg-sub ::edit-deficiency
  :-> ::edit-deficiency)


(rf/reg-sub ::edit-field
  :<- [::edit-deficiency]
  (fn [edit-state [_ key]]
    (-> edit-state key)))


(rf/reg-event-db ::set-edit-field
  (fn [db [_ key data]]
    (assoc-in db [::edit-deficiency key] data)))


(rf/reg-event-fx ::toggle-deficiency
  (fn [{:keys [db]} [_ deficiency-id phase-id cleared?]]
    (let [inspection-id (get db ::inspection-id)
          payload       {:id            deficiency-id
                         :inspection-id inspection-id
                         :domain/state  (if cleared?
                                          :inspection.deficiency.state/tentatively-cleared
                                          :inspection.deficiency.state/active)}
          command       {:command    :process/apply-signal
                         :data       {:entity-id      phase-id
                                      :process/signal [:process.signal/set-inspection-deficiency-state payload]}
                         :on-success [::deficiency-toggled]}]
      {:comm/command command})))


(rf/reg-event-fx ::remove-deficiency
  (fn [{:keys [db]} [_ deficiency-id phase-id success-msg]]
    (let [inspection-id (get db ::inspection-id)
          payload       {:id            deficiency-id
                         :inspection-id inspection-id}]
      {:dispatch [::process/signal
                  phase-id
                  [:process.signal/remove-inspection-deficiency payload]
                  {:notify {:success success-msg}}]})))


(rf/reg-sub ::new-deficiency-data
  :-> ::new-deficiency)


(rf/reg-event-db ::set-create-field
  (fn [db [_ key data]]
    (assoc-in db [::new-deficiency key] data)))


(rf/reg-event-fx ::ai-deficiency-search
  (fn [{:keys [db]} [_ {:keys [inspection-id deficiency-text additional-context part-number reference-type]}]]
    (let [deficiency-description (-> (str deficiency-text " " additional-context) str/trim)]
      {:db (-> db
               (assoc-in [::ai-data :ai-request-loading?] true)
               (assoc-in [::ai-data :search-query] deficiency-text))
       :fx [[:comm/command {:command          :inspection/ai-deficiency-search
                            :method           :post
                            :data             (util/mmap inspection-id deficiency-description part-number reference-type)
                            :on-success       [::ai-deficiency-search.success]
                            :finally-dispatch [::set-ai-loading false]}]]})))


(rf/reg-event-db ::set-ai-loading
  (fn [db [_ loading?]]
    (assoc-in db [::ai-data :ai-request-loading?] loading?)))


(rf/reg-event-fx ::clear-ai-search
  (fn [{:keys [db]} _]
    {:db       (dissoc db ::ai-data)
     :dispatch [::ai-send-negative-feedback (get-in db [::ai-data :trace-id])]}))


(rf/reg-event-db ::ai-deficiency-search.success
  (fn [db [_ _ {:keys [llm-response trace-id]}]]
    (-> db
        (assoc-in [::ai-data :deficiency-search-results] llm-response)
        (assoc-in [::ai-data :visible-panel] :deficiency-search)
        (assoc-in [::ai-data :trace-id] trace-id))))


(rf/reg-event-fx ::ai-send-positive-feedback
  (fn [_ [_ trace-id item-key]]
    {:dispatch [::ai-send-feedback (util/mmap trace-id item-key)]}))


(rf/reg-event-fx ::ai-send-negative-feedback
  (fn [_ [_ trace-id]]
    {:dispatch [::ai-send-feedback (util/mmap trace-id)]}))


(rf/reg-event-fx ::ai-send-feedback
  (fn [{:keys [db]} [_ data]]
    (when (:trace-id data)
      {:comm/command {:command :inspection/ai-send-feedback
                      :method  :post
                      :data    (assoc data :inspection-id (::inspection-id db))}})))


(rf/reg-event-fx ::use-selected-ai-response
  (fn [{:keys [db]} [_ {:keys [item-key reference description]}]]
    (let [old-ref  (-> db ::new-deficiency :reference)
          old-desc (-> db ::new-deficiency :description)
          new-ref  (if-not (str/blank? old-ref)
                     (str old-ref " " reference)
                     reference)
          new-desc (str old-desc "<p>" description "</p>")
          trace-id (get-in db [::ai-data :trace-id])]
      (cond-> {:db (-> db
                       (assoc-in [::new-deficiency :description] new-desc)
                       (assoc-in [::new-deficiency :reference] new-ref)
                       (update-in [::new-deficiency :desc-key] (fnil inc 0))
                       (update-in [::new-deficiency :ref-key] (fnil inc 0))
                       (dissoc ::ai-data))}
        item-key
        (assoc :dispatch [::ai-send-positive-feedback trace-id item-key])))))


(rf/reg-event-fx ::ai-reference-search
  (fn [{:keys [db]} [_ {:keys [inspection-id reference-search-query reference-type]}]]
    {:db (assoc-in db [::ai-data :ai-request-loading?] true)
     :fx [[:comm/command {:command          :inspection/ai-reference-search
                          :method           :post
                          :data             (util/mmap inspection-id reference-search-query reference-type)
                          :on-success       [::ai-reference-search.success]
                          :finally-dispatch [::set-ai-loading false]}]]}))


(rf/reg-event-db ::ai-reference-search.success
  (fn [db [_ _ results]]
    (-> db
        (assoc-in [::ai-data :reference-search-results] results)
        (assoc-in [::ai-data :visible-panel] :reference-search))))


(rf/reg-sub ::get-new-deficiency-data
  :<- [::new-deficiency-data]
  (fn [new-deficiency [_ key default]]
    (get new-deficiency key default)))


(rf/reg-sub ::creating-new-deficiency?
  :<- [::new-deficiency-data]
  :-> :create-open?)


(rf/reg-event-fx ::start-create
  (fn []
    {:fx [[:dispatch [::set-create-field :create-open? true]]]}))


(rf/reg-sub ::workspace-id
  :-> ::workspace-id)


(rf/reg-sub ::photo-linking-enabled?
  :<- [::ff/enabled? :feature-flag.id/deficiency-photo-linking]
  :<- [::ws-subs/has-feature? :group.feature/photo-numbering]
  (fn [[linking-enabled? photo-numbering-enabled?]]
    (and linking-enabled? photo-numbering-enabled?)))


(rf/reg-sub ::labels-enabled?
  :<- [::ws-subs/authority-group]
  :<- [::ff/enabled? :feature-flag.id/inspection-labels]
  :<- [::ff/enabled? :feature-flag.id/inspection-labels-mobile]
  (fn [[auth-group labels? labels-mobile?] _]
    (boolean
      (and auth-group
           (label/labels-enabled? auth-group
                                  {:feature-flag.id/inspection-labels        labels?
                                   :feature-flag.id/inspection-labels-mobile labels-mobile?})))))


(rf/reg-sub ::available-label-groups
  :<- [::workspace]
  (fn [workspace _]
    (->> (label/active-assigned-label-groups workspace)
         (sort-by label/label-group-name))))


(rf/reg-sub ::deficiency-labels
  :<- [::entities/context]
  (fn [ctx [_ deficiency-id]]
    (->> (entities/find-entities ctx {:entity/type   :entity.type/inspection-deficiency-label
                                      :entity/parent deficiency-id})
         (filter label/deficiency-label-active?))))


(rf/reg-event-fx ::add-label
  (fn [{:keys [db]} [_ deficiency-id execution-id label-id]]
    {:dispatch [::process/signal execution-id
                [:process.signal/add-inspection-deficiency-label
                 {:inspection-id (::inspection-id db)
                  :id            deficiency-id
                  :label-id      label-id}]]}))


(rf/reg-event-fx ::remove-label
  (fn [{:keys [db]} [_ deficiency-label-id execution-id]]
    {:dispatch [::process/signal execution-id
                [:process.signal/remove-inspection-deficiency-label
                 {:inspection-id       (::inspection-id db)
                  :deficiency-label-id deficiency-label-id}]]}))


(rf/reg-event-fx ::clear-label
  (fn [{:keys [db]} [_ deficiency-label-id execution-id]]
    {:dispatch [::process/signal execution-id
                [:process.signal/clear-inspection-deficiency-label
                 {:inspection-id       (::inspection-id db)
                  :deficiency-label-id deficiency-label-id}]]}))


(defn photo-label [{:file/keys [number description]}]
  [:div
   (when number [:div.mb-1 number])
   (when description [:div description])])


(defn render-related-photos [selected-photo-ids]
  (let [selected-photos (<sub [::entities/entities selected-photo-ids])]
    [:div.flex.flex-row.flex-wrap.gap-6.mt-2
     (futil/for-frag [{file-id :db/id :as photo} selected-photos]
       [:div.w-64.h-48.bg-center.bg-cover.relative
        {:style {:background-image (gstr/format "url('%s')"
                                                (file-download/download-href :file/download-workspace-file
                                                                             {"file-id" file-id
                                                                              "preview" true}))}}
        [:div.absolute.w-full.bg-black.text-white.p-4.bottom-0.left-0
         [photo-label photo]]])]))


(defn photo-expand-button [tr expanded? on-expand selected-photo-ids can-edit?]
  (let [selected-count (count selected-photo-ids)]
    [ui/expand-button {:button-props    {:size     :small
                                         :kind     :secondary
                                         :disabled (and (= selected-count 0)
                                                        (not can-edit?))}
                       :tr              tr
                       :expanded?       expanded?
                       :expand-handler  on-expand
                       :title-expanded  (tr [:deficiencies/related-photos] [selected-count])
                       :title-collapsed (tr [:deficiencies/related-photos] [selected-count])}]))


(defn render-photo-editing [tr edit-allowed? data-sub set-evt]
  (when (<sub [::photo-linking-enabled?])
    (let [expanded?          (<sub [data-sub :photos-expanded?])
          inspection-id      (<sub [::inspection-id])
          available-photos   (<sub [::photos-for-linking inspection-id])
          selected-photo-ids (<sub [data-sub :selected-photo-ids])]
      [:div.px-1.mb-6.w-full
       [photo-expand-button tr expanded? #(>evt [set-evt :photos-expanded? (not expanded?)]) selected-photo-ids true]
       (when expanded?
         [:div.rounded.border.border-solid.border-gray-500.p-4.mt-2
          (if (<sub [data-sub :selecting-photos?])
            [:div.media-wrapper.my-0
             [photo-select/photo-select (util/mmap {:label-fn         photo-label
                                                    :img-alt-fn       #(str (tr [:inspection/photo]) " " (:file/number %))
                                                    :on-select        #(>evt [set-evt :selected-photo-ids (conj selected-photo-ids %)])
                                                    :on-remove        #(>evt [set-evt :selected-photo-ids (disj selected-photo-ids %)])
                                                    :on-select-all    #(>evt [set-evt :selected-photo-ids (du/entity-id-set available-photos)])
                                                    :on-remove-all    #(>evt [set-evt :selected-photo-ids #{}])
                                                    :on-done          #(>evt [set-evt :selecting-photos? false])
                                                    :download-command :file/download-workspace-file}
                                                   tr
                                                   available-photos
                                                   selected-photo-ids)]]

            [:div
             (when edit-allowed?
               (cc/button {:kind     :primary
                           :size     :small
                           :on-click #(>evt [set-evt :selecting-photos? true])}
                          (tr [:deficiencies/select-related-photos])))
             [render-related-photos selected-photo-ids]])])])))


(defn render-photo-preview [{id :db/id :as deficiency} tr]
  (when (<sub [::ff/enabled? :feature-flag.id/deficiency-photo-linking])
    (let [expanded?          (<sub [::expanded-photo-preview? id])
          selected-photo-ids (->> deficiency :inspection.deficiency/photos du/entity-id-set)]
      [:div.px-1.mb-6.w-full
       [photo-expand-button tr expanded? #(>evt [::toggle-photo-preview-expansion id]) selected-photo-ids false]
       (when expanded?
         [:div.rounded.border.border-solid.border-gray-500.p-4.mt-2
          [render-related-photos selected-photo-ids]])])))


(rf/reg-sub ::ai-data
  :-> ::ai-data)


(rf/reg-sub ::ai-request-loading?
  :<- [::ai-data]
  :-> :ai-request-loading?)


(rf/reg-sub ::ai-deficiency-search-panel-visible?
  :<- [::ai-data]
  (fn [ai-data]
    (= :deficiency-search (:visible-panel ai-data))))


(rf/reg-sub ::ai-deficiency-search-results
  :<- [::ai-data]
  :-> :deficiency-search-results)


(rf/reg-sub ::ai-reference-search-panel-visible?
  :<- [::ai-data]
  (fn [ai-data]
    (= :reference-search (:visible-panel ai-data))))


(rf/reg-sub ::ai-reference-search-results
  :<- [::ai-data]
  :-> :reference-search-results)


(defn- render-date-picker [tr auth-group date-attribute title required? restrict-date?]
  (let [date                       (<sub [::get-new-deficiency-data date-attribute])
        time-slots                 (<sub [::time-slots auth-group])
        default-automatic-booking? (and (<sub [::ff/enabled? :feature-flag.id/deficiency-comply-by-bookings])
                                        (<sub [::ws-subs/has-feature? :group.feature/select-deficiency-for-booking-by-default]))]
    [:div.mr-4
     [ui/date-picker (merge {:id         (str "deficiency-" (name date-attribute))
                             :class-name "full-width-select"
                             :required?  required?
                             :callback   (fn [d]
                                           (>evt [::set-create-field date-attribute d])
                                           (when (and (= date-attribute :comply-by-date)
                                                      default-automatic-booking?)
                                             (>evt [::set-create-field :automatic-booking? true])))
                             :label-text (tr title)
                             :value      date}
                            (when restrict-date?
                              {:allow-date? (butil/allow-date-fn (:group/tz auth-group)
                                                                 time-slots
                                                                 (blackout-model/current-group-blackouts auth-group))}))]]))


(defn- render-remark-checkbox [tr]
  (let [checked (<sub [::get-new-deficiency-data :remark? false])]
    [:div.w-full.md:w-1-2.mt-2.md:mt-6
     (cc/checkbox {:id         "remark-only"
                   :class-name ""
                   :label-text (tr [:deficiencies/remark-only])
                   :checked    checked
                   :on-change  (fn [c] (>evt [::set-create-field :remark? c]))})]))


(defn- render-automatic-booking-checkbox [tr]
  (let [default-automatic? (<sub [::ws-subs/has-feature? :group.feature/select-deficiency-for-booking-by-default])
        checked            (<sub [::get-new-deficiency-data :automatic-booking? default-automatic?])]
    [:div.w-full.md:w-1-2.mt-2.md:mt-6
     (cc/checkbox {:id         "automatic-booking"
                   :label-text (tr [:deficiencies/automatic-booking])
                   :checked    checked
                   :disabled   (nil? (<sub [::get-new-deficiency-data :comply-by-date]))
                   :on-change  (fn [c] (>evt [::set-create-field :automatic-booking? c]))})]))


(defn- render-description-text-area [tr auth-group code-reference-fn]
  (let [description (<sub [::get-new-deficiency-data :description])
        desc-key    (->> (<sub [::get-new-deficiency-data :desc-key])
                         (str "deficiency-description-editor-"))]
    [ui-rt/editor-legacy {:id                      "description"
                          :key                     desc-key
                          :label-text              (tr [:deficiencies/describe-finding])
                          :required?               true
                          :initial-value           description
                          :on-change               #(rf/dispatch-sync [::set-create-field :description %])
                          :default-phrase-category (:group/phrase-category-inspections auth-group)
                          :authority-group-id      (:db/id auth-group)
                          :code-reference-fn       code-reference-fn}]))


(defn- render-reference-text-area [tr auth-group]
  (let [rt-enabled? (<sub [::inspection-subs/checklist-configuration-v2])
        ref-key     (->> (<sub [::get-new-deficiency-data :ref-key])
                         (str "reference-editor-"))
        editor-args {:id         "reference"
                     :key        ref-key
                     :label-text (tr [:deficiencies/reference])}]
    (if rt-enabled?
      [ui-rt/editor-legacy  (merge editor-args
                                   {:authority-group-id (:db/id auth-group)
                                    :initial-value      (<sub [::get-new-deficiency-data :reference])
                                    :on-change          #(rf/dispatch-sync [::set-create-field :reference %])})]
      [ui/text-input (merge editor-args
                            {:value     (<sub [::get-new-deficiency-data :reference])
                             :on-change #(rf/dispatch-sync [::set-create-field :reference (futil/tval %)])})])))


(defn- ai-building-code-item [tr {:keys [reference description explanation] :as item-data}]
  [:div.flex.gap-2.flex-col.justify-between.rounded-md.border-2.border-solid.border-ui-gray.mx-3.p-3
   [:div.flex.flex-col
    [:h5 reference]
    [ui/show-more-text tr 400 (or description explanation)]]
   (when (and description (not explanation))
     [:div.h-min.w-1-5.flex.justify-end.self-end
      [ui/button
       {:kind     :secondary
        :size     :small
        :on-click #(>evt [::use-selected-ai-response item-data])}
       (tr [:inspection-ai/use-code])]])])


(rf/reg-sub ::ai-search-query
  :<- [::ai-data]
  :-> :search-query)


(rf/reg-event-fx ::open-additional-context
  (fn [{:keys [db]} _]
    {:db (assoc-in db [::ai-data :additional-context-open?] true)}))


(rf/reg-sub ::additional-context-open?
  :<- [::ai-data]
  :-> :additional-context-open?)


(rf/reg-event-fx ::set-additional-context
  (fn [{:keys [db]} [_ text]]
    {:db (assoc-in db [::ai-data :additional-context] text)}))


(rf/reg-sub ::additional-context
  :<- [::ai-data]
  :-> :additional-context)


(rf/reg-event-db ::set-part-number
  (fn [db [_ part-number]]
    (assoc-in db [::ai-data :part-number] part-number)))


(rf/reg-sub ::part-number
  :<- [::ai-data]
  (fn [ai-data]
    (get ai-data :part-number "")))


(rf/reg-event-db ::set-reference-type
  (fn [db [_ reference-type]]
    (assoc-in db [::ai-data :reference-type] reference-type)))


(rf/reg-sub ::reference-type
  :<- [::entities/context]
  :<- [::ai-data]
  (fn [[ctx ai-data] [_ inspection-id]]
    (get ai-data :reference-type (-> ctx
                                     (entities/find-entity inspection-id)
                                     building-code-model/inspection-reference-type))))


(rf/reg-event-fx ::ai-deficiency-search-with-refined-parameters
  (fn [{:keys [db]} [_ data]]
    {:db         (assoc-in db [::ai-data :refined-deficiency-search?] true)
     :dispatch-n [[::ai-send-negative-feedback (get-in db [::ai-data :trace-id])]
                  [::ai-deficiency-search data]]}))


(rf/reg-event-fx ::ai-reference-search-with-refined-parameters
  (fn [{:keys [db]} [_ data]]
    {:db       (assoc-in db [::ai-data :refined-reference-search?] true)
     :dispatch [::ai-reference-search data]}))


(rf/reg-event-db ::set-reference-search-query
  (fn [db [_ text]]
    (assoc-in db [::ai-data :reference-search-query] text)))


(rf/reg-sub ::reference-search-query
  :<- [::ai-data]
  :-> :reference-search-query)

(rf/reg-sub ::refined-deficiency-search?
  :<- [::ai-data]
  :-> :refined-deficiency-search?)


(rf/reg-sub ::refined-reference-search?
  :<- [::ai-data]
  :-> :refined-reference-search?)


(defn- ai-deficiency-search-panel [tr inspection-id deficiency-text deficiency-search-results]
  (let [ai-search-query    (<sub [::ai-search-query])
        additional-context (<sub [::additional-context])
        part-number        (<sub [::part-number])
        reference-type     (<sub [::reference-type inspection-id])
        refined-search?    (<sub [::refined-deficiency-search?])
        search             #(>evt [::ai-deficiency-search-with-refined-parameters
                                   (merge (util/mmap inspection-id deficiency-text additional-context
                                                     part-number reference-type)
                                          %)])]
    [:div.bg-white.p-5.rounded.flex.flex-col
     (if (and (empty? deficiency-search-results)
              (not refined-search?))
       [:h4.m-0 (tr [:inspection-ai/deficiency-search-no-codes-found])]
       [:<>
        [:div.flex.justify-between.items-start
         [:div.flex.gap-2
          [:div
           [cc/icon {:icon        i/search
                     :description (tr [:inspection-ai/searching-with])
                     :width       "12px"}]]
          [:div
           [:span.font-bold (tr [:inspection-ai/searching-with]) ": "]
           ai-search-query]]
         [ui-ib/icon-button
          {:icon        i/remove
           :on-click    #(>evt [::clear-ai-search])
           :description (tr [:inspection-ai/close-ai-response-panel])}]]
        [:div.my-3
         (if (<sub [::additional-context-open?])
           [:div.space-y-2
            [ui/text-area {:id         "ai-additional-context"
                           :label-text (tr [:inspection-ai/additional-context])
                           :rows       2
                           :value      additional-context
                           :on-change  #(>evt [::set-additional-context (futil/tval %)])}]
            [ui/button
             {:kind             :primary
              :size             :small
              :icon             i/lightbulb
              :icon-description (tr [:inspection-ai/search])
              :disabled         (str/blank? additional-context)
              :on-click         #(search {})}
             (tr [:inspection-ai/search])]]
           [ui/button
            {:kind             :tertiary
             :size             :small
             :class-name       "text-xs normal-case px-0"
             :icon             i/pen
             :icon-description (tr [:inspection-ai/add-additional-context])
             :on-click         #(>evt [::open-additional-context])}
            (tr [:inspection-ai/add-additional-context])])]
        [:div.flex.flex-col-reverse.lg:flex-row.gap-4.items-center.justify-between.mb-4
         (if (empty? deficiency-search-results)
           [:div]
           [:h4 (tr [:inspection-ai/we-found])])
         [:div.flex.flex-col.lg:flex-row.gap-2
          [:div.flex.items-center.gap-2
           [:span (tr [:inspection-ai/code-reference-type])]
           [ui-select/select {:id              "ai-deficiency-search-reference-type"
                              :external-label? true
                              :allow-empty?    false
                              :values          (-> building-code-model/reference-types keys sort)
                              :val->str        #(-> % building-code-model/reference-types :label)
                              :value           reference-type
                              :on-change       #(do (>evt [::set-reference-type %])
                                                    (>evt [::set-part-number ""])
                                                    (search {:reference-type %
                                                             :part-number    ""}))}]]
          [:div.flex.items-center.gap-2
           [:span (tr [:inspection-ai/code-part])]
           [ui-select/select {:id              "ai-code-part"
                              :external-label? true
                              :allow-empty?    false
                              :values          (->> reference-type
                                                    building-code-model/reference-types
                                                    :parts-count
                                                    inc
                                                    (range 1)
                                                    (map str)
                                                    (cons ""))
                              :val->str        #(if (str/blank? %)
                                                  (tr [:inspection-ai/all-code-parts])
                                                  %)
                              :value           part-number
                              :on-change       #(do (>evt [::set-part-number %])
                                                    (search {:part-number %}))}]]]]
        (if (empty? deficiency-search-results)
          [:h4.my-4 (tr [:inspection-ai/refined-deficiency-search-no-codes-found])]
          [:div.space-y-6
           (futil/for-frag [[item-key item] deficiency-search-results
                            :let            [reference   (str (:Reference_Number item) " " (:Reference_Title item))
                                             description (:Reference_Sentence item)
                                             explanation (:Explain item)]]
             [ai-building-code-item tr (util/mmap item-key reference description explanation)])])])]))


(defn- ai-reference-search-panel [tr inspection-id reference-search-query reference-search-results]
  (let [reference-type  (<sub [::reference-type inspection-id])
        refined-search? (<sub [::refined-reference-search?])]
    [:div.bg-white.p-5.rounded.flex.flex-col
     (if (and (empty? reference-search-results)
              (not refined-search?))
       [:h4.m-0 (tr [:inspection-ai/reference-search-no-codes-found])]
       [:<>
        [:div.flex.justify-end
         [ui-ib/icon-button
          {:icon        i/remove
           :on-click    #(>evt [::clear-ai-search])
           :description (tr [:inspection-ai/close-ai-response-panel])}]]
        [:div.flex.flex-col-reverse.lg:flex-row.gap-4.items-center.justify-between.my-4
         (if (empty? reference-search-results)
           [:div]
           [:h4 (tr [:inspection-ai/we-found])])
         [:div.flex.items-center.gap-2
          [:span (tr [:inspection-ai/code-reference-type])]
          [ui-select/select {:id              "ai-reference-search-reference-type"
                             :external-label? true
                             :allow-empty?    false
                             :values          (-> building-code-model/reference-types keys sort)
                             :val->str        #(-> % building-code-model/reference-types :label)
                             :value           reference-type
                             :on-change       #(do (>evt [::set-reference-type %])
                                                   (>evt [::ai-reference-search-with-refined-parameters
                                                          (merge (util/mmap inspection-id reference-search-query)
                                                                 {:reference-type %})]))}]]]
        (if (empty? reference-search-results)
          [:h4.my-4 (tr [:inspection-ai/refined-reference-search-no-codes-found])]
          [:div.space-y-6.max-h-96.overflow-y-auto
           (futil/for-frag [item-data reference-search-results
                            :let [description (:content item-data)
                                  metadata    (:metadata item-data)
                                  reference   (str (:identifier metadata) " " (:title metadata))]]
             [ai-building-code-item tr (util/mmap reference description)])])])]))


(defn- ai-section [tr inspection-id ai-loading?]
  (let [deficiency-text                  (-> (<sub [::get-new-deficiency-data :description])
                                             rich-text/html-to-text)
        deficiency-search-panel-visible? (<sub [::ai-deficiency-search-panel-visible?])
        deficiency-search-results        (<sub [::ai-deficiency-search-results])
        reference-search-panel-visible?  (<sub [::ai-reference-search-panel-visible?])
        reference-search-results         (<sub [::ai-reference-search-results])
        reference-search-query           (<sub [::reference-search-query])
        refined-search?                  (or (<sub [::refined-deficiency-search?])
                                             (<sub [::refined-reference-search?]))
        ai-deficiency-search-disabled?   (true? (or (str/blank? deficiency-text)
                                                    ai-loading?
                                                    (> (count deficiency-text) building-code-model/ai-request-limit)))
        ai-reference-search-disabled?    (true? (or (str/blank? reference-search-query)
                                                    ai-loading?))
        part-number                      (<sub [::part-number])
        reference-type                   (<sub [::reference-type inspection-id])]
    [:<>
     [ui-sep/horizontal]
     [:div.flex.flex-col.gap-4
      (when (and (empty? deficiency-search-results)
                 (empty? reference-search-results)
                 (not refined-search?))
        [:div.flex.flex-col.items-start.gap-3
         [ui/button
          {:kind             :primary
           :size             :small
           :icon             i/lightbulb
           :icon-description (tr [:inspection-ai/code-search])
           :disabled         ai-deficiency-search-disabled?
           :on-click         #(>evt [::ai-deficiency-search
                                     (util/mmap inspection-id deficiency-text part-number reference-type)])}
          (tr [:inspection-ai/code-search])]
         [:div.flex.gap-2.items-center
          [:div.w-40
           [ui/text-input {:id          "reference-search"
                           :placeholder (tr [:inspection-ai/find-code-reference-placeholder])
                           :hide-label  true
                           :value       reference-search-query
                           :on-change   #(>evt [::set-reference-search-query (futil/tval %)])}]]
          [ui/button
           {:kind             :primary
            :size             :small
            :icon             i/laptop
            :icon-description (tr [:inspection-ai/find-code-reference])
            :disabled         ai-reference-search-disabled?
            :on-click         #(>evt [::ai-reference-search
                                      (util/mmap inspection-id reference-search-query reference-type)])}
           (tr [:inspection-ai/find-code-reference])]]])
      (when deficiency-search-panel-visible?
        [ai-deficiency-search-panel tr inspection-id deficiency-text deficiency-search-results])
      (when reference-search-panel-visible?
        [ai-reference-search-panel tr inspection-id reference-search-query reference-search-results])]
     [ui-sep/horizontal]]))


(declare render-create-deficiency-tags)

(defn- new-deficiency-editor [tr tr-with-ns auth-group create-signal inspection-id]
  (let [phase-id                 (<sub [::phase-id])
        disabled?                (<sub [::editor-submit-disabled?])
        restrict-comply-by-date? (<sub [::ff/enabled? :feature-flag.id/comply-date-restriction])
        comply-by-bookings?      (<sub [::ff/enabled? :feature-flag.id/deficiency-comply-by-bookings])
        has-feature?             (partial group-model/has-feature? auth-group)
        ai-enabled?              (inspection-model/inspection-ai-enabled?
                                   auth-group
                                   (<sub [::ff/enabled? :feature-flag.id/inspection-ai-assistant])
                                   (<sub [::ff/enabled? :feature-flag.id/inspection-ai-assistant-quebec]))
        ai-loading?              (and ai-enabled?
                                      (<sub [::ai-request-loading?]))]
    [:div
     (when ai-loading?
       (cc/loading-spinner))
     [:header.flex.justify-end
      [:button.bg-transparent.border-0.text-blue-600.text-sm
       {:on-click #(>evt [::reset-editor])}
       (tr [:deficiencies/clear-all])]]
     [:div.px-1.mb-6.flex.flex-col.md:flex-row.md:items-center
      [render-date-picker tr auth-group :date [:deficiencies/discovery-date] true false]
      [render-remark-checkbox tr]]
     (when (has-feature? :group.feature/comply-by-date)
       [:div.px-1.mb-6.flex.flex-col.md:flex-row.md:items-center
        [render-date-picker tr auth-group :comply-by-date [:deficiencies/comply-by-date] false restrict-comply-by-date?]
        (when (and comply-by-bookings?
                   (has-feature? :group.feature/bookings-based-on-comply-by-date)
                   (<sub [::get-new-deficiency-data :comply-by-date]))
          [render-automatic-booking-checkbox tr])])
     [:div.px-1.mb-6
      [render-reference-text-area tr auth-group]]
     [:div.px-1.mb-6
      [render-description-text-area tr auth-group #(rf/dispatch-sync [::set-create-field :reference %])]]
     [render-create-deficiency-tags tr]
     (when ai-enabled?
       [:div.px-1.mb-6
        [ai-section tr inspection-id ai-loading?]])
     [render-photo-editing tr true ::get-new-deficiency-data ::set-create-field]
     [:div.flex.justify-end
      [ui-action-buttons/save-cancel-buttons
       (util/mmap {:on-cancel      #(>evt [::close-editor])
                   :on-save        #(>evt [::create phase-id create-signal (tr-with-ns "create-success")])
                   :save-disabled? disabled?
                   :save-tr        :deficiencies/add-and-close}
                  tr)]]]))


(rf/reg-sub ::quick-pick-deficiencies
  :<- [::entities/context]
  :<- [::inspection-id]
  (fn [[ctx inspection-id] _]
    (let [inspection        (entities/find-entity ctx inspection-id)
          template-kind     (inspection-model/inspection-template-kind inspection)
          ws-leaf-config-id (-> inspection inspection-model/workspace workspace-model/leaf-config :db/id)]
      (inspection-model/active-quick-pick-deficiencies template-kind ws-leaf-config-id))))


(rf/reg-event-db ::toggle-selected-quick-pick-deficiency
  (fn [db [_ quick-pick-deficiency-id]]
    (update db ::selected-quick-pick-deficiency-ids
            util/toggle-in-set
            quick-pick-deficiency-id)))


(rf/reg-sub ::selected-quick-pick-deficiency-ids
  :-> ::selected-quick-pick-deficiency-ids)


(rf/reg-event-db ::deselect-all-quick-pick-deficiencies
  (fn [db _]
    (assoc db ::selected-quick-pick-deficiency-ids #{})))


(rf/reg-event-db ::quick-pick-load-more
  (fn [db _]
    (update db ::quick-pick-visible-count (fnil + 5) 5)))


(rf/reg-sub ::quick-pick-visible-count
  (fn [db _]
    (get db ::quick-pick-visible-count 5)))


(rf/reg-event-fx ::increment-quick-pick-deficiency-weight
  (fn [_ [_ quick-pick-deficiency-id inspection-id]]
    {:comm/command {:command :inspection/increment-quick-pick-deficiency-weight
                    :method  :post
                    :data    (util/mmap quick-pick-deficiency-id inspection-id)}}))


(defn quick-pick-deficiencies-table
  ([qpds tr]
   (quick-pick-deficiencies-table qpds tr nil nil))
  ([qpds tr selected-qpd-ids toggle-selected]
   (r/with-let [*expanded-qpd-ids (r/atom #{})]
     (let [col-class              {:class "flex-1 px-2 py-3"}
           expand-col-opts        (merge {:style {:max-width "3rem"}} col-class)
           checkbox-col-opts      (merge {:style {:max-width "2.5rem"}} col-class)
           reference-col-opts     (merge {:style {:max-width "20rem"}} col-class)
           description-col-opts   col-class
           reference-max-length   100
           description-max-length 250]
       [:<>
        [:div.hidden.md:flex.bg-gray-200.rounded-md.font-bold.border-l-6.border-transparent.border-solid.mb-4
         [:div expand-col-opts]
         (when toggle-selected
           [:div checkbox-col-opts])
         [:div reference-col-opts (tr [:deficiencies/reference])]
         [:div description-col-opts (tr [:deficiencies/description])]]
        [:div.space-y-2
         (futil/for-frag [{:db/keys                               [id]
                           :inspection.quick-pick-deficiency/keys [reference description]} qpds
                          :let [expanded?         (contains? @*expanded-qpd-ids id)
                                plain-reference   (rich-text/join-rich-text-content reference)
                                plain-description (rich-text/join-rich-text-content description)
                                expandable?       (or (> (count plain-reference) reference-max-length)
                                                      (> (count plain-description) description-max-length)
                                                      (not (rich-text/single-paragraph? reference))
                                                      (not (rich-text/single-paragraph? description)))
                                toggle-expanded   #(swap! *expanded-qpd-ids util/toggle-in-set id)]]
           [:div.flex.items-center.bg-gray-200.rounded-md.border-l-6.border-ui-dark-gray.border-solid
            (when expandable?
              {:class       (str "hover:bg-ui-blue-10 cursor-pointer"
                                 (when expanded? " bg-white border"))
               :role        "button"
               :tab-index   0
               :on-click    toggle-expanded
               :on-key-down (fn [e]
                              (when (and (contains? #{"Enter" " "} (.-key e))
                                         (not= (-> e .-target .-id) (str "checkbox-" id)))
                                (.preventDefault e)
                                (toggle-expanded)))})
            [:div.flex.justify-center
             expand-col-opts
             (when expandable?
               (cc/icon {:icon        (if expanded?
                                        i/chevron-up
                                        i/chevron-down)
                         :description (tr [:carbon.table.row.expand])}))]
            (when toggle-selected
              [:div
               (merge checkbox-col-opts
                      {:on-click #(.stopPropagation %)})
               (cc/checkbox {:id         (str "checkbox-" id)
                             :label-text (tr [:select/select])
                             :hide-label true
                             :checked    (contains? selected-qpd-ids id)
                             :on-change  #(toggle-selected id)})])
            [:div.flex-1.flex.flex-col.md:flex-row.md:items-center
             [:div.font-bold.md:hidden.px-2.pt-3
              (tr [:deficiencies/reference])]
             [:div
              reference-col-opts
              (if expanded?
                [ui-rt/editor-legacy {:id            (str "reference-editor-" id)
                                      :label-text    (tr [:deficiencies/reference])
                                      :label-class   "font-bold hidden md:block"
                                      :initial-value reference}]
                (ui/summarize-description plain-reference reference-max-length))]
             [:div.font-bold.md:hidden.px-2.pt-3
              (tr [:deficiencies/description])]
             [:div
              description-col-opts
              (if expanded?
                [ui-rt/editor-legacy {:id            (str "description-editor-" id)
                                      :label-text    (tr [:deficiencies/description])
                                      :label-class   "font-bold hidden md:block"
                                      :initial-value description}]
                (ui/summarize-description plain-description description-max-length))]]])]]))))


(defn- quick-pick-list [tr tr-with-ns create-signal inspection-id]
  (let [all-deficiencies            (<sub [::quick-pick-deficiencies])
        visible-count               (<sub [::quick-pick-visible-count])
        visible-deficiencies        (take visible-count all-deficiencies)
        can-show-more-deficiencies? (> (count all-deficiencies) visible-count)
        selected-deficiency-ids     (<sub [::selected-quick-pick-deficiency-ids])
        phase-id                    (<sub [::phase-id])
        entities-context            (<sub [::entities/context])]
    [:div.pt-2
     (if (empty? all-deficiencies)
       [:p (tr [:deficiencies/quick-pick-list-empty])]
       [:<>
        [:p (tr [:deficiencies/quick-pick-list-info])]
        [:div.bg-white.p-4.mb-6
         [quick-pick-deficiencies-table
          visible-deficiencies tr selected-deficiency-ids #(>evt [::toggle-selected-quick-pick-deficiency %])]
         (when can-show-more-deficiencies?
           [:div.flex.justify-center.mt-4
            [ui/button {:kind     :tertiary
                        :size     :small
                        :on-click #(>evt [::quick-pick-load-more])}
             (tr [:show-more])]])]
        [:div.flex.flex-col.sm:flex-row.gap-4.justify-between
         [:div
          (when (> (count selected-deficiency-ids) 1)
            [ui/button {:kind     :tertiary
                        :size     :small
                        :on-click #(>evt [::deselect-all-quick-pick-deficiencies])}
             (tr [:multi-select/select-none])])]
         [ui-action-buttons/save-cancel-buttons
          (util/mmap {:on-cancel      #(>evt [::close-editor])
                      :on-save        #(do
                                         (doseq [deficiency-id selected-deficiency-ids
                                                 :let          [deficiency (entities/find-entity entities-context deficiency-id)]]
                                           (let [reference   (:inspection.quick-pick-deficiency/reference deficiency)
                                                 description (:inspection.quick-pick-deficiency/description deficiency)]
                                             (>evt [::process/signal
                                                    phase-id
                                                    [create-signal (p/assoc-if {:inspection-id                     inspection-id
                                                                                :inspection.deficiency/description description
                                                                                :inspection.deficiency/remark?     false
                                                                                :entity/created                    (js/Date.)}
                                                                               :inspection.deficiency/reference reference)]
                                                    {:notify     {:success (tr-with-ns "create-success")}
                                                     :on-success [::increment-quick-pick-deficiency-weight
                                                                  deficiency-id inspection-id]}])))
                                         (>evt [::close-editor]))
                      :save-disabled? (empty? selected-deficiency-ids)
                      :save-tr        :deficiencies/add-and-close}
                     tr)]]])]))


(defn- add-deficiency-view [tr tr-with-ns auth-group create-signal inspection-id]
  (let [quick-pick-list-enabled? (<sub [::ff/enabled? :feature-flag.id/deficiency-quick-pick-list])]
    [:div.p-4.border.border-solid.border-blue-600.bg-gray-200
     [:h2.mt-2.mb-0.text-lg.font-bold (tr-with-ns "add-new")]
     (if quick-pick-list-enabled?
       [ui-tabs/tabs
        {:tab-content-class "px-0"
         :tabs              [{:id      :new
                              :label   (tr [:deficiencies/new])
                              :content [new-deficiency-editor tr tr-with-ns auth-group create-signal inspection-id]}
                             {:id      :quick-pick
                              :label   (tr [:inspection/quick-pick-list])
                              :content [quick-pick-list tr tr-with-ns create-signal inspection-id]}]}]
       [new-deficiency-editor tr tr-with-ns auth-group create-signal inspection-id])]))


(defn- render-type-changer [tr]
  (let [checked? (<sub [::edit-field :remark?])]
    (cc/checkbox {:id         "remark-only"
                  :class-name ""
                  :label-text (str (tr [:deficiencies/remark]) "?")
                  :checked    checked?
                  :on-change  (fn [c] (>evt [::set-edit-field :remark? c]))})))


(defn- render-date [date editing? date-attribute & [picker-props]]
  (if editing?
    (let [date (<sub [::edit-field date-attribute])]
      [ui/date-picker (merge {:id         (str "deficiency-" (name date-attribute))
                              :width      "100%"
                              :callback   (fn [d] (>evt [::set-edit-field date-attribute d]))
                              :value      date
                              :label-text ""
                              :disabled?  false}
                             picker-props)])
    (if date
      ((<sub [::date-util/date-fn]) date)
      "-")))


(def description-max-length 150)

(defn render-description
  "Render the deficiency description text. If it is longer than description-max-length,
   render a substring of the description and a Show more / Show less button."
  [id key description editing?]
  (if editing?
    (let [desc (<sub [::edit-field key])]
      [ui/text-input {:class     "w-full"
                      :id        id
                      :value     desc
                      :on-change #(rf/dispatch-sync [::set-edit-field key (futil/tval %)])}])
    [ui/summarize-description description description-max-length]))

(defn render-rich-text-description [id key description label auth-group tr editing?]
  [ui-rt/editor-legacy {:id                 id
                        :label-text         (when label (tr [label]))
                        :label-class        "font-bold"
                        :initial-value      (if editing? (<sub [::edit-field key]) description)
                        :on-change          (when editing? #(rf/dispatch-sync [::set-edit-field key %]))
                        :authority-group-id (:db/id auth-group)}])


(defn- deficiency-cleared? [{:domain/keys [state]}]
  (contains?
    #{:inspection.deficiency.state/tentatively-cleared
      :inspection.deficiency.state/cleared}
    state))

(defn full-edit-allowed? [deficiency]
  (du/in-state? :inspection.deficiency.state/draft deficiency))

(defn- render-status [deficiency tr]
  (let [draft? (= (:domain/state deficiency) :inspection.deficiency.state/draft)]
    [:div
     (cond
       (:inspection.deficiency/remark? deficiency) (tr [:deficiencies/remark])
       (deficiency-cleared? deficiency) (tr [:yes])
       :else (tr [:no]))
     (when draft?
       [:span.uppercase (str " / " (tr [:deficiencies/draft]))])]))


(defn- render-external-id [tr phase backend-sys-name deficiency]
  (cond
    (-> deficiency :inspection.deficiency/integration-sync-state (= :in-progress))
    (cc/inline-loading {:description (tr [:process.signal.loading/sync-to-back-end-system]
                                         [backend-sys-name])})

    (and (-> deficiency :inspection.deficiency/integration-sync-state (= :not-synced))
         (<sub [::process/signal-permitted?
                phase
                [:process.signal/resend-violation-to-integration
                 {:deficiency-id (:db/id deficiency)}]]))
    (cc/button {:kind     :tertiary
                :size     :small
                :on-click #(>evt [::process/signal
                                  phase
                                  [:process.signal/resend-violation-to-integration
                                   {:deficiency-id (:db/id deficiency)}]])}
               (tr [:synchronization/resend]))

    :else
    (:inspection.deficiency/external-id deficiency "EXTERNAL-ID")))


(defn- render-delete-button [id phase-id tr tr-with-ns]
  [ui/modal-confirmation-button
   {:id                  "delete-title"
    :tr                  tr
    :modal-title         (tr [:deficiencies/delete-title])
    :trigger-button-text (tr [:delete])
    :trigger-button-opts {:icon          i/trash
                          :icon-position :right
                          :size          :small
                          :kind          :tertiary
                          :class-name    "mr-2"}
    :on-yes-click        #(>evt [::remove-deficiency id phase-id (tr-with-ns "remove-success")])}
   [:span (tr [:deficiencies/confirm-delete?])]])


(defn- render-info-field [title children]
  [:div.my-4.ml-8.min-w-0.w-full
   [:div.bold.text-xs.pb-2 title]
   [:div.text-sm children]])

(defn- render-number [{:inspection.deficiency/keys [number]} tr]
  (when (and number (<sub [::ff/enabled? :feature-flag.id/deficiency-photo-numbering]))
    [render-info-field
     (tr [:deficiencies/id])
     number]))

(defn- render-reference [deficiency tr editing?]
  (let [rt-enabled? (<sub [::inspection-subs/checklist-configuration-v2])
        workspace   (-> deficiency :entity/parent :entity/parent :entity/parent)]
    (when (<sub [::show-reference-for-visit?])
      [render-info-field
       (tr [:deficiencies/reference])
       (if rt-enabled?
         [render-rich-text-description
          "deficiency-reference"
          :reference
          (:inspection.deficiency/reference deficiency)
          nil
          (-> workspace :domain/authority-group)
          tr
          (and editing? (full-edit-allowed? deficiency))]
         [render-description
          "deficiency-reference"
          :reference
          (-> deficiency :inspection.deficiency/reference rich-text/join-rich-text-content)
          (and editing? (full-edit-allowed? deficiency))])])))


(defn- render-compliance-date [deficiency tr]
  (let [cleared    (-> deficiency :inspection.deficiency/cleared)
        cleared-at (if cleared
                     ((<sub [::date-util/date-fn]) cleared)
                     "-")]
    [render-info-field
     (tr [:deficiencies/compliance-date])
     cleared-at]))


(defn- render-discovery-date [deficiency tr editing?]
  [render-info-field
   (tr [:deficiencies/discovery-date])
   [render-date (-> deficiency :entity/created) (and editing? (full-edit-allowed? deficiency)) :date]])


(defn- render-integration-info [deficiency tr phase]
  (let [show-ext-id?     (<sub [::display-external-id?])
        backend-sys-name (when show-ext-id?
                           (<sub [::ws-subs/backend-system-name]))]
    (when show-ext-id?
      [render-info-field
       (str backend-sys-name " " (tr [:backend-system/id]))
       [render-external-id tr phase backend-sys-name deficiency]])))


(defn- render-actions [deficiency tr tr-with-ns editing? visit-ongoing? phase inspection-id]
  (let [id                     (:db/id deficiency)
        can-edit?              (<sub [::process/signal-permitted?
                                      phase
                                      [:process.signal/edit-inspection-deficiency
                                       {:id            id
                                        :inspection-id inspection-id}]])
        removable?             (<sub [::process/signal-permitted?
                                      phase
                                      [:process.signal/remove-inspection-deficiency
                                       {:id            id
                                        :inspection-id inspection-id}]])
        can-toggle-compliance? (<sub [::process/signal-permitted?
                                      phase
                                      [:process.signal/set-inspection-deficiency-state
                                       {:id            id
                                        :inspection-id inspection-id
                                        :domain/state  :inspection.deficiency.state/tentatively-cleared}]])
        remark?                (:inspection.deficiency/remark? deficiency)]
    [(if remark?
       :div.flex.flex-wrap.justify-end.items-center
       :div.flex.flex-wrap.justify-between.items-center)
     (when-not remark?
       [:div.flex.items-center
        [:span.mx-2 (tr [:deficiencies/compliant])]
        (cc/toggle {:id         (str id "-complaint-toggle")
                    :disabled   (not can-toggle-compliance?)
                    :class-name "unlabeled-toggle"
                    :size       :sm
                    :labelA     (tr [:no])
                    :labelB     (tr [:yes])
                    :aria-label (tr [:deficiencies/compliant])
                    :toggled    (deficiency-cleared? deficiency)
                    :on-toggle  (fn [cleared?]
                                  (>evt [::toggle-deficiency id (:db/id phase) cleared?]))})])
     [:div.flex.flex-wrap
      (when (and visit-ongoing? removable?)
        [render-delete-button id (:db/id phase) tr tr-with-ns])
      (when (and can-edit? (not editing?))
        (cc/button {:kind     :tertiary
                    :size     :small
                    :on-click #(>evt [::start-editing deficiency])}
                   (tr [:edit])))
      (when editing?
        [ui-action-buttons/save-cancel-buttons
         {:on-cancel #(>evt [::stop-editing])
          :on-save   #(>evt [::save (:db/id phase) (tr-with-ns "changes-saved")])
          :size      :small
          :tr        tr}])]]))


(defn- tag-chip [{:keys [tr name cleared? removable? on-remove on-clear]}]
  [ui-tags/tag
   {:label-text (if cleared? [:span.line-through.text-gray-400 name] name)
    :buttons    (cond-> []
                  removable?
                  (conj {:icon        i/remove
                         :description (tr [:inspection-labels/remove-label])
                         :on-click    on-remove})
                  (and on-clear (not cleared?))
                  (conj {:icon        i/check
                         :description (tr [:inspection-labels/mark-complete])
                         :on-click    on-clear}))}])


(defn- add-labels-toggle [tr]
  [:span.bx--btn.bx--btn--tertiary.bx--btn--sm.text-xs.font-semibold.uppercase.gap-2
   [:span (tr [:inspection-labels/add-labels])]
   (cc/icon {:icon        i/plus
             :description ""
             :width       "14"
             :height      "14"
             :fill        "currentColor"
             :aria-hidden true})])


(defn- add-tags-picker [tr deficiency-id execution-id groups applied]
  (let [applied-label-ids (->> applied (map #(-> % :inspection.deficiency-label/label :db/id)) set)]
    [ui-grouped-menu/grouped-menu
     {:toggle-props   {:class "p-0"}
      :toggle-element [add-labels-toggle tr]
      :sections       (for [g groups]
                        {:title      (label/label-group-name g)
                         :empty-text (tr [:inspection-labels/no-labels])
                         :options    (for [lbl (->> (label/active-labels g)
                                                    (remove #(contains? applied-label-ids (:db/id %))))]
                                       {:label    (label/label-name lbl)
                                        :on-click #(>evt [::add-label deficiency-id execution-id (:db/id lbl)])})})}]))


(defn- add-create-tags-picker [tr groups selected-label-ids]
  [ui-grouped-menu/grouped-menu
   {:toggle-props   {:class "p-0"}
    :toggle-element [add-labels-toggle tr]
    :sections       (for [g groups]
                      {:title      (label/label-group-name g)
                       :empty-text (tr [:inspection-labels/no-labels])
                       :options    (for [lbl (->> (label/active-labels g)
                                                  (remove #(contains? selected-label-ids (:db/id %))))]
                                     {:label    (label/label-name lbl)
                                      :on-click #(>evt [::set-create-field
                                                        :selected-label-ids
                                                        (conj selected-label-ids (:db/id lbl))])})})}])


(defn- render-create-deficiency-tags [tr]
  (when (<sub [::labels-enabled?])
    (let [selected-label-ids (<sub [::get-new-deficiency-data :selected-label-ids #{}])
          groups             (<sub [::available-label-groups])
          labels             (->> groups
                                  (mapcat label/active-labels)
                                  (filter #(contains? selected-label-ids (:db/id %)))
                                  (sort-by label/label-name))]
      [:div.px-1.mb-6.w-full
       [:div.bold.text-xs.pb-2 (tr [:inspection-labels/labels])]
       [:div.flex.flex-col.gap-2
        [:div.flex.flex-wrap.items-center
         (if (seq labels)
           (futil/for-frag [lbl labels]
             [tag-chip {:tr         tr
                        :name       (label/label-name lbl)
                        :removable? true
                        :on-remove  #(>evt [::set-create-field
                                            :selected-label-ids
                                            (disj selected-label-ids (:db/id lbl))])}])
           [:span.text-sm.text-gray-400.mr-2 (tr [:inspection-labels/no-labels-added])])]
        (when (seq groups)
          [add-create-tags-picker tr groups selected-label-ids])]])))


(defn- render-deficiency-tags [deficiency tr phase inspection-id]
  (when (<sub [::labels-enabled?])
    (let [id           (:db/id deficiency)
          execution-id (:db/id phase)
          applied      (<sub [::deficiency-labels id])
          groups       (<sub [::available-label-groups])
          ;; Resolve the signal against the inspection's own phase, like `render-actions` does.
          ;; `::process/execution-signal-permitted?` looks the phase up via workspace view-data,
          ;; which this page never populates.
          can-edit?    (<sub [::process/signal-permitted?
                              phase
                              [:process.signal/edit-inspection-deficiency
                               {:id id :inspection-id inspection-id}]])]
      [:div.px-1.mb-6.w-full
       [:div.bold.text-xs.pb-2 (tr [:inspection-labels/labels])]
       [:div.flex.flex-col.gap-2
        [:div.flex.flex-wrap.items-center
         (if (seq applied)
           (futil/for-frag [dl applied]
             [tag-chip {:tr         tr
                        :name       (label/label-name (:inspection.deficiency-label/label dl))
                        :cleared?   (label/deficiency-label-cleared? dl)
                        :removable? can-edit?
                        :on-remove  #(>evt [::remove-label (:db/id dl) execution-id])
                        :on-clear   #(>evt [::clear-label (:db/id dl) execution-id])}])
           [:span.text-sm.text-gray-400.mr-2 (tr [:inspection-labels/no-labels-added])])]
        (when (and can-edit? (seq groups))
          [add-tags-picker tr id execution-id groups applied])]])))


(defn render-expanded-func [deficiency tr tr-with-ns editing? visit-ongoing? phase inspection-id]
  [:div
   [ui-sep/horizontal]
   [:div.flex.flex-wrap
    [render-number deficiency tr]
    [render-reference deficiency tr editing?]
    [render-compliance-date deficiency tr]
    [render-discovery-date deficiency tr editing?]
    [render-integration-info deficiency tr phase]
    [render-deficiency-tags deficiency tr phase inspection-id]
    (if editing?
      [render-photo-editing tr (full-edit-allowed? deficiency) ::edit-field ::set-edit-field]
      [render-photo-preview deficiency tr])]
   [ui-sep/horizontal]
   [render-actions deficiency tr tr-with-ns editing? visit-ongoing? phase inspection-id]])

(defn- render-show-all-checkbox [tr-with-ns]
  (let [checked? (<sub [::show-all?])]
    [:div.mb-2
     (cc/checkbox {:id         "show-all"
                   :class-name ""
                   :label-text (tr-with-ns "show-all")
                   :checked    checked?
                   :on-change  #(>evt [::toggle-show-all])})]))


(defn- render-tag-filter [tr]
  (when (<sub [::labels-enabled?])
    (let [labels   (<sub [::available-labels])
          selected (<sub [::tag-filter-ids])]
      (when (seq labels)
        [:div.mb-2.max-w-xs
         [ui-ms/multi-select
          {:id         "deficiency-tag-filter"
           :label-text (tr [:inspection-labels/filter-by-label])
           :values     (mapv :id labels)
           :value      selected
           :val->str   (into {} (map (juxt :id :name)) labels)
           :on-change  #(>evt [::set-tag-filter %])
           :class-name "w-full"}]]))))

;; Helper functions for history tabs
(defn- get-inspection-name [deficiency lang]
  (let [inspection (:entity/parent deficiency)]
    (or (inspection-model/inspection-name inspection lang) "")))

(defn- get-inspection-status [deficiency tr]
  (let [inspection (:entity/parent deficiency)
        state      (:domain/state inspection)]
    (tr [(keyword "inspection.state" (name state))])))

(defn- render-open-inspection-button [deficiency tr-with-ns]
  (let [inspection    (:entity/parent deficiency)
        workspace     (inspection-model/workspace inspection)
        workspace-id  (:workspace/id workspace)
        inspection-id (:db/id inspection)
        href          (href/href :view/inspection-visit
                                 {:workspace-id  workspace-id
                                  :inspection-id inspection-id})]
    (cc/button {:kind     :tertiary
                :size     :small
                :on-click #(futil/open-in-new-tab-or-window href)}
               (tr-with-ns "open-inspection"))))

(defn- render-history-expanded-content [deficiency tr tr-with-ns]
  (let [inspection-id         (-> deficiency :entity/parent :db/id)
        current-inspection-id (<sub [::inspection-id])]
    [:div
     [ui-sep/horizontal]
     [:div.flex.flex-wrap
      [render-number deficiency tr]
      [render-reference deficiency tr false]
      [render-compliance-date deficiency tr]
      [render-discovery-date deficiency tr false]]
     [render-photo-preview deficiency tr]
     (when (not= inspection-id current-inspection-id)
       [:<>
        [ui-sep/horizontal]
        [:div.flex.justify-end.mt-4
         [render-open-inspection-button deficiency tr-with-ns]]])]))

(defn inspection-name-col [lang]
  [:deficiencies/inspection-name
   (fn [deficiency]
     [:span (get-inspection-name deficiency lang)])
   {:sorting true}])

(defn inspection-status-col [tr]
  [:deficiencies/status
   (fn [deficiency]
     [:span (get-inspection-status deficiency tr)])
   {:sorting true
    :width   "8rem"}])

(defn deficiency-compliant-col [tr]
  [:deficiencies/compliant
   (fn [deficiency]
     [:span (if (deficiency-cleared? deficiency)
              (tr [:yes])
              (tr [:no]))])
   {:sorting true
    :width   "7rem"}])

(defn deficiency-description-col [{:keys [expanded-row-keys auth-group editing? tr]}]
  [:deficiencies/description
   (fn [deficiency]
     (if (contains? expanded-row-keys (:db/id deficiency))
       [:div
        [render-rich-text-description
         "deficiency-description"
         :description
         (:inspection.deficiency/description deficiency)
         nil
         auth-group
         tr
         (and editing?
              (editing? deficiency)
              (full-edit-allowed? deficiency))]]
       [:div
        [render-description
         "deficiency-description"
         :description
         (-> deficiency :inspection.deficiency/description rich-text/join-rich-text-content)
         false]]))
   {:sorting (fn [& deficiency-pair]
               (->> deficiency-pair
                    (map #(-> % :inspection.deficiency/description rich-text/join-rich-text-content))
                    (apply compare)))}])

(defn deficiency-comply-by-date-col [comply-by-date?]
  (if comply-by-date?
    [:deficiencies/comply-by-date
     (fn [deficiency]
       (if-let [date (:inspection.deficiency/comply-by-date deficiency)]
         ((<sub [::date-util/date-fn]) date)
         "-"))
     {:sorting (fn [& deficiency-pair]
                 (->> deficiency-pair
                      (map :inspection.deficiency/comply-by-date)
                      (map #(if (nil? %) js/Number.MAX_VALUE (.getTime %)))
                      (apply compare)))
      :width   "11rem"}]
    [:deficiencies/compliance-date
     (fn [deficiency]
       (if-let [date (:inspection.deficiency/cleared deficiency)]
         ((<sub [::date-util/date-fn]) date)
         "-"))
     {:sorting (fn [& deficiency-pair]
                 (->> deficiency-pair
                      (map :inspection.deficiency/cleared)
                      (map #(if (nil? %) js/Number.MAX_VALUE (.getTime %)))
                      (apply compare)))
      :width   "7rem"}]))

;; New version (sortable-table)
(defn- render-deficiencies [visit-ongoing? inspection-id auth-group phase tr tr-with-ns]
  (let [deficiencies             (<sub [::deficiencies])
        editing-id               (<sub [::edit-field :id])
        editing?                 (fn [deficiency]
                                   (= editing-id (:db/id deficiency)))
        expanded-row-keys        (<sub [::expanded-rows])
        restrict-comply-by-date? (<sub [::ff/enabled? :feature-flag.id/comply-date-restriction])
        time-slots               (<sub [::time-slots auth-group])
        has-feature?             (-> auth-group :group/features set)]
    [sortable-table/table
     {:id                :inspection-deficiencies-v2
      :tr                tr
      :cols              [[:deficiencies/compliant
                           (fn [deficiency]
                             (if (and (editing? deficiency)
                                      (full-edit-allowed? deficiency))
                               [render-type-changer tr]
                               [render-status deficiency tr]))
                           {:sorting (fn [& deficiency-pair]
                                       (->> deficiency-pair (map #(str (render-status % tr))) (apply compare)))
                            :width   "7rem"}]
                          (deficiency-description-col (util/mmap expanded-row-keys auth-group editing? tr))
                          (if (has-feature? :group.feature/comply-by-date)
                            [:deficiencies/comply-by-date
                             (fn [deficiency]
                               [render-date
                                (:inspection.deficiency/comply-by-date deficiency)
                                (and (editing? deficiency)
                                     (contains? expanded-row-keys (:db/id deficiency)))
                                :comply-by-date
                                (when restrict-comply-by-date?
                                  {:allow-date? (butil/allow-date-fn (:group/tz auth-group)
                                                                     time-slots
                                                                     (blackout-model/current-group-blackouts auth-group))})])
                             {:sorting true
                              :width   "11rem"}]
                            [:deficiencies/compliance-date
                             (fn [deficiency]
                               [render-date (:inspection.deficiency/cleared deficiency) false nil])
                             {:sorting true
                              :width   "7rem"}])]
      :items             deficiencies
      :expanded-func     (fn [deficiency]
                           [render-expanded-func
                            deficiency
                            tr
                            tr-with-ns
                            (editing? deficiency)
                            visit-ongoing?
                            phase
                            inspection-id])
      :expanded-row-keys expanded-row-keys
      :on-expand         #(>evt [::toggle-row-expansion %])
      :get-status        (fn [{:domain/keys [state] :as deficiency}]
                           (if (:inspection.deficiency/remark? deficiency)
                             :notification
                             (case state
                               :inspection.deficiency.state/draft :warning
                               :inspection.deficiency.state/active :critical
                               :ok)))}]))

;; All Deficiencies tab table
(defn- render-all-deficiencies-table [auth-group tr tr-with-ns]
  (let [deficiencies      (<sub [::all-workspace-deficiencies-no-remarks])
        expanded-row-keys (<sub [::history-expanded-rows])
        has-feature?      (-> auth-group :group/features set)
        lang              (<sub [:language])]
    [:div
     [render-show-all-checkbox tr-with-ns]
     (if (seq deficiencies)
       [sortable-table/table
        {:id                :all-workspace-deficiencies
         :tr                tr
         :cols              [(inspection-name-col lang)
                             (inspection-status-col tr)
                             (deficiency-compliant-col tr)
                             (deficiency-description-col (util/mmap expanded-row-keys auth-group tr))
                             (deficiency-comply-by-date-col (has-feature? :group.feature/comply-by-date))]
         :items             deficiencies
         :expanded-func     (fn [deficiency]
                              [render-history-expanded-content deficiency tr tr-with-ns])
         :expanded-row-keys expanded-row-keys
         :on-expand         #(>evt [::toggle-history-row-expansion %])
         :get-status        (fn [{:domain/keys [state]}]
                              (case state
                                :inspection.deficiency.state/draft :warning
                                :inspection.deficiency.state/active :critical
                                :ok))}]
       [:div.mt-4.text-gray-600 (tr-with-ns "no-workspace-items")])]))

;; All Remarks tab table
(defn- render-all-remarks-table [auth-group tr tr-with-ns]
  (let [remarks           (<sub [::all-workspace-remarks])
        expanded-row-keys (<sub [::history-expanded-rows])
        lang              (<sub [:language])]
    (if (seq remarks)
      [sortable-table/table
       {:id                :all-workspace-remarks
        :tr                tr
        :cols              [(inspection-name-col lang)
                            (inspection-status-col tr)
                            (deficiency-description-col (util/mmap expanded-row-keys auth-group tr))]
        :items             remarks
        :expanded-func     (fn [deficiency]
                             [render-history-expanded-content deficiency tr tr-with-ns])
        :expanded-row-keys expanded-row-keys
        :on-expand         #(>evt [::toggle-history-row-expansion %])
        :get-status        (constantly :notification)}]
      [:div.mt-4.text-gray-600 (tr [:deficiencies/no-workspace-remarks])])))

(def tab-keys [:current :all-items :all-remarks])

(def deficiencies-section-id "deficiencies-section")

(defn- render-current-inspection-tab
  [{:keys [visit-ongoing? inspection-id auth-group target-entity tr tr-with-ns
           creating-new-deficiency? create-signal deficiencies? visible-deficiencies?]}]
  [:div.deficiencies
   (when visit-ongoing?
     (if creating-new-deficiency?
       [add-deficiency-view tr tr-with-ns auth-group create-signal inspection-id]
       (cc/button {:kind             :primary
                   :size             :small
                   :icon             i/plus
                   :icon-description ""
                   :on-click         #(>evt [::start-create])}
                  (tr-with-ns "add-new"))))
   (if deficiencies?
     [:div.mt-2
      [render-show-all-checkbox tr-with-ns]
      [render-tag-filter tr]
      (if visible-deficiencies?
        [render-deficiencies visit-ongoing? inspection-id auth-group target-entity tr tr-with-ns]
        (when-not visit-ongoing?
          [:p.p-0.my-4 (tr-with-ns "all-cleared")]))]
     [:div.mt-4 (tr-with-ns "no-items")])])

(defn render-deficiency-list
  [{:keys [tr auth-group target-entity workspace] :as props}]
  (let [create-signal            (<sub [::create-signal])
        creating-new-deficiency? (<sub [::creating-new-deficiency?])
        deficiencies?            (<sub [::deficiencies?])
        visible-deficiencies?    (<sub [::visible-deficiencies?])
        inspection-id            (<sub [::inspection-id])
        visit-ongoing?           (<sub [::process/signal-permitted? target-entity [create-signal {:inspection-id inspection-id}]])
        i18n-ns                  (if (contains? inspection-model/violation-permit-types
                                                (:workspace/permit-type workspace))
                                   "violations"
                                   "deficiencies")
        tr-with-ns               #(tr [(keyword i18n-ns %)])
        active-tab               (<sub [::active-tab])]
    [section/section
     deficiencies-section-id
     props
     (tr-with-ns "section-heading")
     [:div
      ;; Tab switcher
      [:div.mb-4
       [content-switcher/content-switcher
        {:items     tab-keys
         :val->str  tr-with-ns
         :current   active-tab
         :on-change #(>evt [::set-active-tab %])}]]
      ;; Tab content
      (case active-tab
        :all-items
        [render-all-deficiencies-table auth-group tr tr-with-ns]

        :all-remarks
        [render-all-remarks-table auth-group tr tr-with-ns]

        ;; Default to current tab
        [render-current-inspection-tab
         (util/mmap visit-ongoing?
                    inspection-id
                    auth-group
                    target-entity
                    tr
                    tr-with-ns
                    creating-new-deficiency?
                    create-signal
                    deficiencies?
                    visible-deficiencies?)])]]))
